// fence.ts — Meal Tracker's MEDICAL/ADVICE fence, enforced PRE-LLM (deterministic, zero deps).
//
// Extracted from brain.ts so a battery (fence-battery.mjs) can exercise it WITHOUT importing
// Tauri/Supabase. brain.ts re-exports these, so every existing caller is unchanged. Same shape
// as oriora_hivewise::fence.ts, oriora_findlog::fence.ts and oriora_cellar::fence.ts.
//
// This is the HIGHEST-STAKES fence in the fleet: health. Wrong advice here is not a bad
// recommendation, it is a medical one. The app is observational by contract — it describes the
// person's OWN logged figures and nothing else — so diagnosis, medication, diet prescription,
// targets, and "should I…" are refused here, for free, before any billed call and out of reach
// of a prompt injection aimed at the server prompt.
//
// Broad by intent: refusing a borderline phrasing is the safe failure; answering one is not.
// The battery's PASS column exists to prove that breadth hasn't swallowed the product — the app
// must still answer "what did I log on Tuesday".

export const MEDICAL_PATTERNS: RegExp[] = [
  /\bdiagnos/i,
  /\bsymptom|disease|illness|condition\b/i,
  // NO trailing \b — these are PREFIXES (diabet→diabetes, hypertens→hypertension). A trailing
  // \b makes them unmatchable, which is how "do i have diabetes?" walked straight through.
  /\b(diabet|cholesterol|blood pressure|hypertens|thyroid|pregnan|coeliac|celiac|kidney|liver|cancer|anemi|anaemi)/i,
  /\b(ibs|heart)\b/i,
  /\bmedicat|medicine|prescri|supplement|vitamin d(?:ose)?|insulin|ozempic|wegovy/i,
  /\ballerg(y|ic|en)/i,
  /\b(eating disorder|anorexi|bulimi|binge)\b/i,
  /\bshould i (eat|stop|start|take|skip|fast|cut|weigh|lose|gain)\b/i,
  /\bhow (much|many)\b[^?]{0,40}\b(should|do i need|is (right|enough|ok|okay))\b/i,
  /\bwhat should i (eat|weigh|do)\b/i,
  /\b(is|are) (this|that|it|my|the)( \w+){0,2} (safe|healthy|unhealthy|ok|okay|good|bad|fine|too much)\b/i,
  /\b(meal|diet) plan\b/i,
  /\b(keto|paleo|fasting|intermittent|low[- ]carb|detox|cleanse)\b/i,
  /\b(lose|losing|drop|cut) weight\b/i,
  /\bweight[- ]loss\b/i,
  /\btarget (weight|calories)|calorie (goal|target|deficit|limit)\b/i,
  /\bam i (fat|overweight|underweight|obese|too |eating too|drinking too|consuming too|not eating)/i,
  /\bbmi\b/i,
];

export const MEDICAL_REFUSAL =
  "I can describe what you've logged, not give medical, diet, or health advice — for that, please talk to a doctor or registered dietitian.";

/** Returns the refusal message if the free-text question trips the medical/advice
 *  fence, else null. The caller blocks the AI call when this returns a string. */
export function medicalRefusal(question: string): string | null {
  const q = question.trim();
  if (!q) return null;
  return MEDICAL_PATTERNS.some((re) => re.test(q)) ? MEDICAL_REFUSAL : null;
}
